/**
 * Drifts a `.slider` horizontally at a constant speed, wrapping seamlessly.
 *
 * Why scrollLeft rather than the theme's `marquee` keyframes (main.css): these
 * tiles sit inside `.slider`, a native overflow-x scroller that OverlayScrollbars
 * mounts a progress bar on and that CarouselSlider's arrows read scrollLeft from.
 * Animating a transform would move the content without moving the scroller, so
 * the progress bar and arrows would report a position that is not what is on
 * screen. Driving scrollLeft keeps all three reading the same number.
 *
 * The seamless wrap needs a duplicated tile set. CarouselSlider measures slide
 * geometry in its own init(), so the clones are appended only after it has run
 * (it sets `inactive` off / the nav up by then) and are marked aria-hidden with
 * tabindex="-1" so the duplicate links are invisible to assistive tech and to
 * tab order. Wrapping subtracts one whole set width, which puts the same tiles
 * back in the same screen positions. The grid's percentage columns make the true
 * set width fractional, so a tile can land a sub-pixel off across a wrap; at
 * 24px/s that is well under one frame of travel and does not read as a jump.
 *
 * Trap: the arrows call scrollTo({behavior:'smooth'}), which the drift would
 * fight frame by frame. Any pointer, wheel or arrow input therefore stops the
 * drift outright and restarts it only after the input has been quiet for
 * data-resume-delay, rather than easing against the user's own scrolling.
 */
class AutoScrollCarousel extends HTMLElement {
  connectedCallback() {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.reduceMotion.matches) return;

    window.initLazyScript(this, this.init.bind(this));
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.resizeHandler) window.removeEventListener('on:debounced-resize', this.resizeHandler);
  }

  init() {
    this.slider = this.querySelector('.slider');
    this.grid = this.querySelector('.slider__grid');
    if (!this.slider || !this.grid) return;

    this.speed = parseFloat(this.dataset.speed) || 24;
    this.resumeDelay = parseInt(this.dataset.resumeDelay, 10) || 2500;
    this.paused = 0;
    this.interacting = false;

    // Wait for CarouselSlider to finish measuring before the clones land.
    requestAnimationFrame(() => requestAnimationFrame(() => this.setup()));
  }

  setup() {
    if (!this.originals) {
      this.originals = Array.from(this.grid.children);
    }
    if (this.originals.length < 2) return;

    this.clearClones();
    this.measure();
    if (this.setWidth <= 0 || this.slider.clientWidth <= 0) return;

    this.watchLayout();

    if (this.setWidth <= this.slider.clientWidth + 1) return;

    this.addClones();
    this.addListeners();
    this.start();
  }

  watchLayout() {
    if (this.observer || !('ResizeObserver' in window)) return;

    this.observer = new ResizeObserver(() => {
      const before = this.setWidth;
      this.measure();
      if (Math.abs(this.setWidth - before) < 1) return;

      const copies = Math.max(1, Math.ceil(this.slider.clientWidth / this.setWidth) + 1);
      if (!this.clones || this.clones.length !== copies * this.originals.length) {
        this.clearClones();
        this.addClones();
      }
      if (this.slider.scrollLeft >= this.setWidth) {
        this.slider.scrollLeft %= this.setWidth;
      }
    });

    this.observer.observe(this.grid);
    this.originals.forEach((item) => this.observer.observe(item));
  }

  /**
   * Distances here are whole pixels because scrollLeft is quantised to whole
   * pixels; a fractional wrap distance cannot be expressed through it.
   */
  measure() {
    const first = this.originals[0].getBoundingClientRect();
    const last = this.originals[this.originals.length - 1].getBoundingClientRect();
    const gap = parseFloat(window.getComputedStyle(this.grid).columnGap) || 0;
    this.setWidth = Math.round((last.right - first.left) + gap);
  }

  addClones() {
    const copies = Math.max(1, Math.ceil(this.slider.clientWidth / this.setWidth) + 1);
    this.clones = [];

    for (let i = 0; i < copies; i += 1) {
      this.originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-cc-animate');
        clone.removeAttribute('data-cc-animate-delay');
        clone.classList.add('is-visible');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.grid.appendChild(clone);
        this.clones.push(clone);
      });
    }
  }

  clearClones() {
    if (!this.clones) return;
    this.clones.forEach((clone) => clone.remove());
    this.clones = null;
  }

  addListeners() {
    if (this.listening) return;
    this.listening = true;

    this.pause = this.hold.bind(this);
    this.resume = this.release.bind(this);

    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);
    this.addEventListener('touchstart', this.pause, { passive: true });
    this.addEventListener('touchend', this.resume, { passive: true });

    this.interactHandler = this.handleInteract.bind(this);
    this.slider.addEventListener('pointerdown', this.interactHandler);
    this.slider.addEventListener('wheel', this.interactHandler, { passive: true });
    this.nav = this.querySelector('.slider-nav');
    if (this.nav) this.nav.addEventListener('click', this.interactHandler);

    this.visibilityHandler = () => {
      if (document.hidden) this.stop(); else this.start();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener('on:debounced-resize', this.resizeHandler);

    this.motionHandler = () => {
      if (this.reduceMotion.matches) {
        this.stop();
        this.clearClones();
      } else {
        this.setup();
      }
    };
    this.reduceMotion.addEventListener('change', this.motionHandler);
  }

  handleResize() {
    this.stop();
    this.slider.scrollLeft = 0;
    this.setup();
  }

  // Counted, not boolean: pointer-over and focus-within can overlap.
  hold() {
    this.paused += 1;
    this.stop();
  }

  release() {
    this.paused = Math.max(0, this.paused - 1);
    if (this.paused === 0 && !this.interacting) this.start();
  }

  handleInteract() {
    this.interacting = true;
    this.stop();

    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.interacting = false;
      if (this.paused === 0) this.start();
    }, this.resumeDelay);
  }

  start() {
    if (this.rafId || this.paused > 0 || this.interacting || document.hidden) return;
    if (this.reduceMotion.matches) return;

    this.lastFrame = null;
    this.pos = this.slider.scrollLeft;
    this.rafId = requestAnimationFrame(this.step.bind(this));
  }

  stop() {
    if (!this.rafId) return;
    cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  step(now) {
    if (this.lastFrame === null) this.lastFrame = now;
    const elapsed = now - this.lastFrame;
    this.lastFrame = now;

    // Cap the delta so a backgrounded tab does not resume with a large jump.
    this.pos += (this.speed * Math.min(elapsed, 50)) / 1000;

    if (this.pos >= this.setWidth) this.pos -= this.setWidth;
    this.slider.scrollLeft = this.pos;

    this.rafId = requestAnimationFrame(this.step.bind(this));
  }
}

customElements.define('auto-scroll-carousel', AutoScrollCarousel);
